/**
 * Custom hook for managing role assumption functionality in the application.
 *
 * This hook provides a clean interface for components to initiate role assumption
 * by dispatching the necessary actions to save user information and navigate to
 * the appropriate dashboard view. It also owns the auto-resume-on-reload behavior:
 * if a username is already known from sessionStorage (via assumedUserName, seeded
 * at getInitialRoleAssumptionState), it auto-resubmits it instead of requiring the
 * user to re-type it.
 *
 * @returns {Object} Hook interface containing:
 *   - username: current username input value
 *   - assumeRole: Function to initiate role assumption with a username
 *   - isValidRoleAssumptionUser: whether the last validated username was accepted
 *   - requestStatus: current ApiRequestStatus of the role assumption validation call
 *   - handleSubmit: form submit handler that assumes the current username
 *   - handleInputChange: input change handler that updates the username and clears a stale validation error
 *
 * @example
 * ```tsx
 * const { assumeRole } = useRoleAssumptionHook();
 *
 * const handleRoleAssumption = () => {
 *   assumeRole('john.doe@example.com');
 * };
 * ```
 */

import type { FormEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { TextInputEventValue } from '@interstate/components/TextInput';
import { middlewareMgr, resetValidationState, roleAssumptionSelectors, validateRoleAssumptionUser } from '../store';
import { useRoleAssumptionSelector } from '../selectors';
import { ApiRequestStatus } from '@makemydeal/dr-shared-types';

export const useRoleAssumptionHook = () => {
    const dispatch = useDispatch();
    const { getValidateUserURL } = roleAssumptionSelectors.additionalSelectors;
    const bffEndpoint = getValidateUserURL();
    const { assumedUserName, isValidRoleAssumptionUser, requestStatus } = useRoleAssumptionSelector();

    const [username, setUsername] = useState('');
    const hasAutoResumed = useRef(false);

    /**
     * Initiates role assumption process by saving the username and navigating to dashboard.
     *
     * This function performs two key actions:
     * 1. Saves the role assumption username to the store
     * 2. Dispatches navigation to the dashboard
     *
     * @param usernameToAssume - The username/identifier of the user to assume role for
     */
    const assumeRole = useCallback(
        (usernameToAssume: string) => {
            // Save the username for the role assumption session
            dispatch(middlewareMgr.hostActions!.saveRoleAssumptionUserName(usernameToAssume));

            //Validate the role assumption user
            if (bffEndpoint) {
                dispatch(validateRoleAssumptionUser(bffEndpoint));
            }
        },
        [dispatch]
    );

    const handleSubmit = useCallback(
        (e: FormEvent) => {
            e.preventDefault();
            if (username.trim()) {
                const startWorkflowEvaluatorAction = middlewareMgr.hostActions!.startWorkflowEvaluator?.();
                if (startWorkflowEvaluatorAction) {
                    dispatch(startWorkflowEvaluatorAction);
                }
                assumeRole(username);
            }
        },
        [username, assumeRole]
    );

    const handleInputChange = useCallback(
        (e: InterstateOnChangeEvent<TextInputEventValue>) => {
            setUsername(e.target.value);
            // Reset validation state when user changes the input
            if (!isValidRoleAssumptionUser) {
                dispatch(resetValidationState());
            }
        },
        [isValidRoleAssumptionUser, dispatch]
    );

    // On a fresh page load/reload, assumedUserName is already seeded from sessionStorage
    // (see getInitialRoleAssumptionState) if role assumption was previously resolved this
    // session. Auto-resubmit it instead of forcing the user to re-type their username.
    useEffect(() => {
        if (assumedUserName && requestStatus === ApiRequestStatus.Idle && !hasAutoResumed.current) {
            hasAutoResumed.current = true;
            setUsername(assumedUserName);
            assumeRole(assumedUserName);
        }
    }, [assumedUserName, requestStatus, assumeRole]);

    // Return the interface for consuming components
    return {
        username,
        assumeRole, // Function to initiate role assumption with a username
        isValidRoleAssumptionUser,
        requestStatus,
        handleSubmit,
        handleInputChange
    };
};
